import React from "react";

/**
 * Tiny inline SVG sparkline (oldest -> newest). `null` values are skipped (gaps).
 * tone: "auto" colours by trend of the recent half vs. the earlier half (green up / red down);
 *       "inverse" flips that (for metrics where rising is bad, e.g. temperature);
 *       "neutral" always uses the primary colour.
 */
export const Sparkline = ({ values = [], width = 96, height = 28, testId, tone = "auto" }) => {
  const n = values.length;
  if (!n) return null;
  const nums = values.map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)));
  const present = nums.filter((v) => v != null);
  if (present.length === 0) {
    return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="text-muted-foreground" data-testid={testId} aria-hidden="true"><line x1="2" y1={height / 2} x2={width - 2} y2={height / 2} stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" /></svg>;
  }
  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const pad = 2;
  const stepX = n > 1 ? (width - pad * 2) / (n - 1) : 0;
  const pts = nums.map((v, i) => (v == null ? null : [pad + i * stepX, height - pad - ((v - min) / span) * (height - pad * 2)]));
  // Break the polyline at gaps.
  const segments = [];
  let cur = [];
  pts.forEach((p) => { if (p) cur.push(p); else if (cur.length) { segments.push(cur); cur = []; } });
  if (cur.length) segments.push(cur);
  const half = Math.floor(n / 2);
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const recent = avg(nums.slice(half).filter((v) => v != null));
  const earlier = avg(nums.slice(0, half).filter((v) => v != null));
  let dir = 0;
  if (recent != null && earlier != null) dir = recent > earlier ? 1 : recent < earlier ? -1 : 0;
  if (tone === "inverse") dir = -dir;
  const cls = tone === "neutral" || dir === 0
    ? "text-primary"
    : dir > 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]";
  const last = [...pts].reverse().find(Boolean);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`${cls} overflow-visible`} data-testid={testId} aria-hidden="true">
      {segments.map((seg, i) => (
        <polyline key={i} points={seg.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {pts.map((p, i) => (p && (nums[i - 1] == null || nums[i + 1] == null) ? <circle key={`d${i}`} cx={p[0]} cy={p[1]} r="1.8" fill="currentColor" /> : null))}
      {last && <circle cx={last[0]} cy={last[1]} r="2.2" fill="currentColor" />}
    </svg>
  );
};
